import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import api, { apiErrorDetail } from "../lib/api";
import Reveal from "../components/Reveal";

const STEPS = [
  { key: "created", label: "ORDER PLACED" },
  { key: "paid", label: "PAYMENT CONFIRMED" },
  { key: "submitted_to_qikink", label: "IN PRODUCTION" },
  { key: "processing", label: "QUALITY CHECK" },
  { key: "shipped", label: "SHIPPED" },
  { key: "delivered", label: "DELIVERED" },
];

const stageIndex = { pending_payment: 1, paid: 2, submitted_to_qikink: 3, processing: 4, shipped: 5, delivered: 6, cancelled: 0 };

export default function OrderConfirmation() {
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [rEmail, setREmail] = useState("");
  const [rReason, setRReason] = useState("");
  const [rBusy, setRBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/orders/${orderNumber}`);
      setOrder(data);
      return data;
    } catch (e) {
      setErr(apiErrorDetail(e));
      return null;
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [orderNumber]);

  const sync = async () => {
    setSyncing(true);
    try {
      await api.get(`/orders/${orderNumber}`);
      await load();
      toast.success("Order status refreshed");
    } catch (e) {
      toast.error(apiErrorDetail(e));
    }
    setSyncing(false);
  };

  const submitReturn = async (e) => {
    e.preventDefault();
    setRBusy(true);
    try {
      const { data } = await api.post("/returns/request", {
        order_number: order.order_number, email: rEmail, reason: rReason,
      });
      toast.success(data.message || "Return requested");
      setREmail(""); setRReason("");
      await load();
    } catch (e2) {
      toast.error(apiErrorDetail(e2));
    }
    setRBusy(false);
  };

  if (err) {
    return <main className="pt-40 pb-24 text-center font-mono text-[11px] tracking-widest text-volt">{err}</main>;
  }
  if (!order) {
    return <main className="pt-40 pb-24 text-center font-mono text-[11px] tracking-widest text-mute">PULLING YOUR ORDER…</main>;
  }

  const cancelled = order.status === "cancelled";
  const current = cancelled ? 0 : stageIndex[order.status] || 1;
  const qk = order.qikink || {};
  const awaitingPayment = order.status === "pending_payment" && order.payment.method === "prepaid";
  const restockMode = order.fulfilment?.mode === "restock" || order.fulfilment?.hint === "restock_available";
  const ret = order.return || { status: "" };
  const showReturnForm = order.status === "delivered" && (!ret.status || ret.status === "rejected");

  return (
    <main className="px-5 md:px-10 pt-28 pb-24 max-w-[1100px] mx-auto">
      <Reveal>
        <p className="font-mono text-[11px] tracking-[0.3em] text-acid mb-3">
          {cancelled ? "ORDER CANCELLED" : awaitingPayment ? "AWAITING PAYMENT" : "ORDER CONFIRMED"}
        </p>
        <h1 className="font-display font-extrabold uppercase tracking-tighter text-3xl sm:text-5xl">
          {cancelled ? "THIS ORDER WAS CANCELLED" : awaitingPayment ? "COMPLETE YOUR PAYMENT" : "WELCOME TO THE CULT."}
        </h1>
        {awaitingPayment && (
          <p className="mt-4 font-mono text-xs tracking-widest text-[#3B82F6]">WAITING FOR THE PAYMENT TO CLEAR — THIS PAGE UPDATES AUTOMATICALLY.</p>
        )}
        <p className="mt-4 font-mono text-xs tracking-widest text-dim">
          ORDER <span data-testid="order-number" className="text-acid">{order.order_number}</span> · {new Date(order.created_at).toLocaleString("en-IN")}
        </p>
      </Reveal>

      {/* STATUS CARD */}
      <Reveal delay={0.08}>
        <div data-testid="order-status-card" className="mt-10 border border-white/10 bg-[#0E1015] p-6 md:p-10">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
            <p className="font-mono text-[11px] tracking-[0.3em] text-dim">ORDER STATUS</p>
            <button onClick={sync} data-testid="order-sync-btn" className="flex items-center gap-2 font-mono text-[10px] tracking-widest border border-white/20 px-3 py-1.5 hover:border-acid hover:text-acid transition-colors">
              <RefreshCw size={11} className={syncing ? "animate-spin" : ""} /> REFRESH
            </button>
          </div>

          <div className="space-y-0">
            {STEPS.map((s, i) => {
              const done = !cancelled && i + 1 <= current && (s.key !== "submitted_to_qikink" || qk.submitted || restockMode);
              const label = s.key === "submitted_to_qikink" && restockMode && !qk.submitted ? "FROM OUR STUDIO" : s.label;
              return (
                <div key={s.key} className="flex items-stretch gap-5">
                  <div className="flex flex-col items-center">
                    <div className={`w-3 h-3 mt-1.5 ${done ? "bg-acid" : "border border-white/25"}`} />
                    {i < STEPS.length - 1 && <div className={`w-px flex-1 min-h-[34px] ${i + 1 < current ? "bg-acid" : "bg-white/15"}`} />}
                  </div>
                  <div className="pb-7">
                    <p className={`font-display font-bold text-sm tracking-widest ${done ? "text-ink" : "text-mute"}`}>
                      {s.key === "paid" && order.payment.method === "cod" ? "COD — PAY ON DELIVERY" : label}
                    </p>
                    {s.key === "submitted_to_qikink" && qk.submitted && (
                      <p className="font-mono text-[10px] text-mute mt-1">YOUR ORDER IS IN OUR PRODUCTION QUEUE</p>
                    )}
                    {s.key === "submitted_to_qikink" && restockMode && !qk.submitted && (
                      <p className="font-mono text-[10px] text-mute mt-1">YOUR UNIT SHIPS FROM OUR STUDIO — NOT A NEW PRINT</p>
                    )}
                    {s.key === "shipped" && (qk.awb || qk.courier) && (
                      <p className="font-mono text-[10px] text-acid mt-1">TRACKING: {qk.awb || "—"} {qk.courier ? `· ${qk.courier}` : ""}</p>
                    )}
                    {s.key === "shipped" && order.shipping?.awb && (
                      <p data-testid="restock-tracking-line" className="font-mono text-[10px] text-acid mt-1">
                        TRACKING: {order.shipping.awb} {order.shipping.courier ? `· ${order.shipping.courier}` : ""}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {order.fulfilment?.hint === "restock_available" && !order.fulfilment?.mode && (
            <p data-testid="restock-hint-note" className="font-mono text-[10px] text-acid mt-2">
              GOOD NEWS — A RESTOCKED UNIT MATCHES YOUR ORDER. IT SHIPS DIRECTLY FROM OUR STUDIO.
            </p>
          )}
          {qk.error && !qk.submitted && (
            <p data-testid="order-queued-note" className="font-mono text-[10px] text-mute mt-2">
              YOUR ORDER IS CONFIRMED AND QUEUED FOR PRODUCTION — WE'LL EMAIL YOUR TRACKING LINK AS SOON AS IT SHIPS. QUESTIONS? SUPPORT@RVIOR.IN
            </p>
          )}
        </div>
      </Reveal>

      {/* RETURN STATUS / REQUEST */}
      {ret.status && ret.status !== "rejected" && (
        <Reveal delay={0.06}>
          <div data-testid="return-status-card" className="mt-6 border border-white/10 bg-[#0E1015] p-6 md:p-8">
            <p className="font-mono text-[11px] tracking-[0.3em] text-dim mb-3">RETURN STATUS</p>
            <p className="font-display font-bold text-sm tracking-widest text-acid">
              {ret.status === "requested" ? "RETURN REQUESTED — UNDER REVIEW" : ret.status === "approved" ? "RETURN APPROVED — SEND THE TEE BACK" : "REFUND PROCESSED"}
            </p>
            <p className="font-mono text-[11px] text-dim mt-2 leading-relaxed">
              {ret.status === "refunded"
                ? `REFUND OF ₹${Number(ret.refund_amount || 0).toLocaleString("en-IN")} WAS SENT TO YOUR ORIGINAL PAYMENT METHOD — IT USUALLY LANDS IN 5-7 BUSINESS DAYS.`
                : ret.status === "approved"
                ? "PACK THE TEE (UNWORN, TAGS ON) AND SHIP IT TO US — YOUR REFUND IS INITIATED THE DAY IT REACHES US AND PASSES CHECK."
                : "WE'LL REVIEW AND CONFIRM BY EMAIL. APPROVED RETURNS ARE REFUNDED MINUS A MINIMUM ₹65 RETURN-SHIPPING FEE."}
            </p>
          </div>
        </Reveal>
      )}
      {showReturnForm && (
        <Reveal delay={0.06}>
          <div data-testid="return-request-card" className="mt-6 border border-white/10 bg-[#0E1015] p-6 md:p-8">
            <p className="font-mono text-[11px] tracking-[0.3em] text-volt mb-3">CHANGE OF MIND? 7-DAY RETURNS</p>
            <form onSubmit={submitReturn} className="space-y-3 max-w-xl">
              <input
                data-testid="return-email-input" type="email" required
                placeholder="EMAIL USED ON THE ORDER" value={rEmail} onChange={(e) => setREmail(e.target.value)}
                className="w-full bg-[#151821] border border-white/15 px-4 py-3 font-mono text-xs text-ink placeholder:text-mute focus:outline-none focus:border-acid"
              />
              <textarea
                data-testid="return-reason-input" required minLength={5} rows={3}
                placeholder="WHY ARE YOU RETURNING? (SIZE / FIT / OTHER)" value={rReason} onChange={(e) => setRReason(e.target.value)}
                className="w-full bg-[#151821] border border-white/15 px-4 py-3 font-mono text-xs text-ink placeholder:text-mute focus:outline-none focus:border-acid"
              />
              <button data-testid="return-submit-btn" disabled={rBusy} type="submit" className="bg-volt text-void font-display font-bold text-xs tracking-widest px-8 py-3.5 hover:bg-acid transition-colors disabled:opacity-40">
                {rBusy ? "SENDING…" : "REQUEST RETURN"}
              </button>
              <p className="font-mono text-[9px] tracking-widest text-mute">
                APPROVED RETURNS ARE REFUNDED MINUS A MINIMUM ₹65 RETURN-SHIPPING FEE. TEE MUST BE UNWORN WITH TAGS.
              </p>
            </form>
          </div>
        </Reveal>
      )}

      <div className="grid md:grid-cols-2 gap-6 mt-6">
        <Reveal delay={0.12}>
          <div className="border border-white/10 bg-[#0E1015] p-6 h-full">
            <p className="font-mono text-[11px] tracking-[0.3em] text-dim border-b border-white/10 pb-3 mb-5">UNITS</p>
            <div className="space-y-4">
              {order.items.map((it, i) => (
                <div key={i} className="flex gap-3">
                  <img src={it.image} alt={it.title} className="w-12 h-14 object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-[11px] font-bold uppercase truncate">{it.title}</p>
                    <p className="font-mono text-[10px] text-mute mt-0.5">{it.color} / {it.size} × {it.qty}</p>
                  </div>
                  <p className="font-mono text-xs">₹{(it.price * it.qty).toLocaleString("en-IN")}</p>
                </div>
              ))}
            </div>
            <div className="border-t border-white/10 mt-5 pt-4 space-y-1.5 font-mono text-xs">
              <div className="flex justify-between text-dim"><span>SUBTOTAL</span><span>₹{order.pricing.subtotal.toLocaleString("en-IN")}</span></div>
              {order.pricing.cod_fee > 0 && <div className="flex justify-between text-dim"><span>COD FEE</span><span>₹{order.pricing.cod_fee}</span></div>}
              <div className="flex justify-between font-bold text-sm pt-2"><span>TOTAL</span><span className="text-acid">₹{order.pricing.total.toLocaleString("en-IN")}</span></div>
              <p className="text-[10px] text-mute pt-1">PAYMENT: {order.payment.method.toUpperCase()} ({(order.payment.status || "").toUpperCase()})</p>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.16}>
          <div className="border border-white/10 bg-[#0E1015] p-6 h-full">
            <p className="font-mono text-[11px] tracking-[0.3em] text-dim border-b border-white/10 pb-3 mb-5">DISPATCH TO</p>
            <p className="font-display text-sm font-bold tracking-widest">{order.customer.first_name} {order.customer.last_name}</p>
            <p className="font-mono text-[11px] text-dim mt-3 leading-relaxed">
              {order.address.address1}<br />
              {order.address.address2 && <>{order.address.address2}<br /></>}
              {order.address.city}, {order.address.state} — {order.address.pincode}<br />
              {order.customer.phone} · {order.customer.email}
            </p>
            <Link to="/shop" className="inline-block mt-8 border border-white/20 font-mono text-[10px] tracking-[0.25em] px-6 py-3 hover:border-acid hover:text-acid transition-colors">
              KEEP SHOPPING
            </Link>
          </div>
        </Reveal>
      </div>
    </main>
  );
}
